import React from "react";
import "../../css/autobiography_popup.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPenToSquare,
  faTimes,
  faXmark,
  faMicrophone,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import { useState } from "react";
import {
  editDiary,
  getDiary,
  getSingleDiary,
  getUser,
  getWritingCenter,
  saveDiary,
  uploadDiaryAudio,
  getDiaryAudio,
} from "../../services/user-service";
import { useRef } from "react";
import { toast } from "react-toastify";
import JoditEditor from "jodit-react";
import "./../../css/popupcontent.css";
import { Tooltip } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";

// Autocomplete Input Component
function AutocompleteInput({ value, onChange, placeholder, className }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isMouseOverSuggestions, setIsMouseOverSuggestions] = useState(false);

  const commonTitles = [
    "Today's Thoughts",
    "Daily Reflection",
    "Morning Journal",
    "Evening Notes",
    "Weekend Adventures",
    "Travel Memories",
    "Personal Growth",
    "Gratitude Journal",
    "Dream Log",
    "Creative Ideas",
    "Work Progress",
    "Life Lessons",
    "Family Time",
    "Special Moments",
    "Goals and Dreams",
    "Inspirations",
    "Lessons Learned",
    "Happy Memories",
    "Challenges Overcome",
    "New Beginnings",
  ];

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(e);

    if (inputValue.length > 0) {
      const filtered = commonTitles.filter((title) =>
        title.toLowerCase().includes(inputValue.toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion) => {
    const syntheticEvent = {
      target: {
        value: suggestion,
        name: "entry-title",
      },
    };
    onChange(syntheticEvent);
    setShowSuggestions(false);
  };

  const handleBlur = () => {
    // Only hide if mouse is not over suggestions
    if (!isMouseOverSuggestions) {
      setShowSuggestions(false);
    }
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        type="text"
        name="entry-title"
        className={className}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={() => {
          if (value.length > 0) {
            const filtered = commonTitles.filter((title) =>
              title.toLowerCase().includes(value.toLowerCase())
            );
            setSuggestions(filtered);
            setShowSuggestions(filtered.length > 0);
          }
        }}
        spellCheck={true}
        autoComplete="off"
      />
      {showSuggestions && (
        <div
          onMouseEnter={() => setIsMouseOverSuggestions(true)}
          onMouseLeave={() => setIsMouseOverSuggestions(false)}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "white",
            border: "1px solid #ddd",
            borderRadius: "4px",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 1000,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            marginTop: "4px",
          }}
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => selectSuggestion(suggestion)}
              style={{
                padding: "10px 12px",
                cursor: "pointer",
                borderBottom:
                  index < suggestions.length - 1 ? "1px solid #f0f0f0" : "none",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#f5f5f5")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "white")}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Shared Jodit Editor Config with FIXED spell check
const getEditorConfig = () => ({
  placeholder: "Start Typing.....",
  spellcheck: true,
  language: "en",
  toolbarButtonSize: "small",
  buttons: [
    "bold",
    "italic",
    "underline",
    "|",
    "ul",
    "ol",
    "|",
    "font",
    "fontsize",
    "|",
    "align",
    "|",
    "undo",
    "redo",
  ],
  showCharsCounter: false,
  showWordsCounter: true,
  showXPathInStatusbar: false,
  enter: "P",
  defaultActionOnPaste: "insert_clear_html",
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  defaultLineHeight: 1.5,
  // CRITICAL: These settings enable spell check
  iframe: false, // Don't use iframe mode
  inline: false,
  tabIndex: 1,
  // Force spell check on the contenteditable element
  events: {
    afterInit: function (editor) {
      // Get the actual contenteditable element
      const editorElement = editor.editor || editor.element;
      if (editorElement) {
        // Force spellcheck attributes
        editorElement.setAttribute("spellcheck", "true");
        editorElement.setAttribute("lang", "en-US");
        editorElement.contentEditable = "true";

        // Also set it on the container if it exists
        const container = editor.container;
        if (container) {
          const editableDiv = container.querySelector(
            '[contenteditable="true"]'
          );
          if (editableDiv) {
            editableDiv.setAttribute("spellcheck", "true");
            editableDiv.setAttribute("lang", "en-US");
          }
        }
      }
    },
    // Re-apply spellcheck after any content change
    afterSetMode: function (editor) {
      const editorElement = editor.editor || editor.element;
      if (editorElement) {
        editorElement.setAttribute("spellcheck", "true");
        editorElement.setAttribute("lang", "en-US");
      }
    },
  },
});

// Custom Hook for Speech Recognition
function useSpeechRecognition(setContent) {
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = "en-US";

      recognitionRef.current.onresult = (event) => {
        let finalTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + " ";
          }
        }

        if (finalTranscript) {
          setContent((prevContent) => prevContent + finalTranscript);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsRecording(false);
        if (event.error === "not-allowed") {
          toast.error(
            "Microphone access denied. Please enable microphone permissions."
          );
        } else {
          toast.error("Speech recognition error. Please try again.");
        }
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [setContent]);

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      toast.error("Speech recognition is not supported in your browser.");
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      recognitionRef.current.start();
      setIsRecording(true);
      toast.info("Listening... Speak now!");
    }
  };

  const stopRecording = () => {
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  return { isRecording, toggleRecording, stopRecording };
}

// Speech-to-Text Button Component
function SpeechToTextButton({ isRecording, toggleRecording }) {
  return (
    <div style={{ marginBottom: "10px" }}>
      <Tooltip title={isRecording ? "Stop recording" : "Start speech-to-text"}>
        <button
          type="button"
          onClick={toggleRecording}
          style={{
            padding: "10px 20px",
            backgroundColor: isRecording ? "#dc3545" : "#007bff",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "background-color 0.3s",
          }}
          onMouseEnter={(e) => {
            if (!isRecording) {
              e.target.style.backgroundColor = "#0056b3";
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = isRecording
              ? "#dc3545"
              : "#007bff";
          }}
        >
          <FontAwesomeIcon icon={isRecording ? faStop : faMicrophone} />
          {isRecording ? "Stop Recording" : "Start Voice Input"}
        </button>
      </Tooltip>
    </div>
  );
}

export function Diarypopup() {
  const { book_id } = useParams();
  const [editingIndex, setEditingIndex] = useState(null);
  const [data, setData] = useState({
    heading: "",
    note: "",
    user: {
      id: getUser().id,
    },
    writingCenter: {
      id: book_id,
    },
  });
  const [content, setContent] = useState("");
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingTimeout = useRef(null);
  const editor = useRef();

  const { isRecording, toggleRecording, stopRecording } =
    useSpeechRecognition(setContent);
    const MAX_DURATION = 60000; // 1 min limit

    const startRecordingAudio = async () => {
        setAudioBlob(null);
        setAudioUrl(null);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        chunksRef.current = [];

        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunksRef.current.push(e.data);
          }
        };

        mediaRecorder.onstop = () => {
          const blob = new Blob(chunksRef.current, {
            type: "audio/webm;codecs=opus",
          });

          // SIZE LIMIT (2MB)
          if (blob.size > 2 * 1024 * 1024) {
            toast.error("Audio too large! Max 2MB allowed.");
            return;
          }

          setAudioBlob(blob);
          setAudioUrl(URL.createObjectURL(blob));
        };

        mediaRecorder.start();
        setIsRecordingAudio(true);

        setSeconds(0);

        timerRef.current = setInterval(() => {
          setSeconds((prev) => prev + 1);
        }, 1000);

        // AUTO STOP AFTER 1 MIN
        recordingTimeout.current = setTimeout(() => {
          stopRecordingAudio();
          toast.info("Recording stopped (1 min limit)");
        }, MAX_DURATION);

      } catch (error) {
        toast.error("Microphone permission denied");
      }
    };

    const stopRecordingAudio = () => {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
        setIsRecordingAudio(false);
        clearTimeout(recordingTimeout.current);
        clearInterval(timerRef.current);
          // ✅ IMPORTANT: stop microphone completely
            if (mediaRecorderRef.current.stream) {
              mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
              }
      }
    };
  const config = getEditorConfig();

  const handleChanges = (event, property) => {
    setData({ ...data, [property]: event.target.value });
  };

  const resetData = () => {
    setData({
      heading: "",
      note: "",
      user: {
        id: getUser().id,
      },
    });
    setContent("");
      setAudioBlob(null);
      setAudioUrl(null);
      setSeconds(0);
      clearInterval(timerRef.current);
  };

  const handleEditSave = (id) => {
    const updatedDiary = {
      heading: data.heading,
      note: content,
      user: {
        id: getUser().id,
      },
      writingCenter: {
        id: book_id,
      },
    };

    editDiary(id, updatedDiary)
      .then((data) => {
        resetData();
      })
//       .catch((error) => {});
         .catch((error) => {
           toast.error("Something went wrong");
         })
         .finally(() => {
           setIsSubmitting(false);   // ✅ ADD THIS
         });
  };

//   const handleFormSubmit = (event) => {
    const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    stopRecording();

    if (editingIndex !== null) {
      handleEditSave(editingIndex);
      setEditingIndex(null);
    } else {
      data.note = content;
      if (data.heading === "" || data.note === "" || data.user.id === "") {
        toast.error("Please add all the fields... ");
        setIsSubmitting(false);
        return;
      }
       if (isRecordingAudio) {
         stopRecordingAudio();
         await new Promise(res => setTimeout(res, 800)); // ⏱ wait for blob
       }
      saveDiary(data)
          .then(async (resp) => {
            const diaryId = resp.data.id;

              // upload audio if exists
              if (audioBlob && audioBlob.size > 0) {
                const formData = new FormData();
                formData.append("file", audioBlob);
                try {
                  await uploadDiaryAudio(diaryId, formData);
                } catch (err) {
                  toast.error("Audio upload failed");
                }
              }
          toast.success("Data Added Successfully", {
            position: toast.POSITION.BOTTOM_CENTER,
          });
          resetData();
          window.history.back();
        })
        .catch((error) => {});
    }
  };

  return (
    <div className="popup">
      <div className="popup-content">
        <section className="user_diary_add_note">
          <form
            className="user_diary_add_note_form"
            id="entryForm"
            onSubmit={handleFormSubmit}
          >
            <a onClick={() => window.history.back()}>
              <div className="login_cancel_icon">
                <FontAwesomeIcon className="cancel_icon_login" icon={faTimes} />
              </div>
            </a>
            <label htmlFor="entry-title" className="user_diary_label">
              Enter Diary Title...
            </label>

            <AutocompleteInput
              value={data.heading}
              onChange={(e) => handleChanges(e, "heading")}
              placeholder="Title of the Entry ✏️"
              className="user_diary_entry_text_title"
            />

            <label htmlFor="entry" className="user_diary_label">
              Start Your Creation...
            </label>

            <SpeechToTextButton
              isRecording={isRecording}
              toggleRecording={toggleRecording}
            />

            <div style={{ marginBottom: "10px" }}>
                {isRecordingAudio && (
                  <p style={{ color: "red", fontWeight: "bold" }}>
                    Recording: {seconds}s ⏱️
                  </p>
                )}
              {/* <button
                type="button"
               onClick={isRecordingAudio ? stopRecordingAudio : startRecordingAudio}
                style={{
                  padding: "10px 15px",
                  backgroundColor: isRecordingAudio ? "#dc3545" : "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                // {isRecordingAudio ? "Stop Voice Memo ⏹️" : "Start Voice Memo 🎙️"}
              </button> */}
            </div>

             {audioUrl && (
               <div style={{ marginBottom: "10px" }}>
                 <audio controls src={audioUrl}></audio>
               </div>
             )}
            <JoditEditor
              ref={editor}
              value={content}
              config={config}
              onBlur={(newContent) => setContent(newContent)}
            />
{/*             <button className="user_diary_submit_btn" type="submit"> */}
               <button
                 className="user_diary_submit_btn"
                 type="submit"
                 disabled={isSubmitting}
                 style={{
                   opacity: isSubmitting ? 0.6 : 1,
                   cursor: isSubmitting ? "not-allowed" : "pointer",
                 }}
               >
              <Tooltip title="Click to save your page">
{/*                 {editingIndex !== null ? "Save" : "Submit"} */}
                     {isSubmitting ? "Saving..." : editingIndex !== null ? "Save" : "Submit"}
              </Tooltip>
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}

export function EditDiaryPopup() {
  const { page_id, books_id } = useParams();
  const [data, setData] = useState({
    heading: "",
    note: "",
    user: {
      id: getUser().id,
    },
    writingCenter: {
      id: books_id,
    },
  });
  const [content, setContent] = useState("");
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const editor = useRef();

  const { isRecording, toggleRecording, stopRecording } =
    useSpeechRecognition(setContent);
  const config = getEditorConfig();

  const startRecordingAudio = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);

    mediaRecorderRef.current = mediaRecorder;
    chunksRef.current = [];

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      setAudioBlob(blob);
    };

    mediaRecorder.start();
    setIsRecordingAudio(true);
  };

  const stopRecordingAudio = () => {
    mediaRecorderRef.current.stop();
    setIsRecordingAudio(false);
  };

  const fetchData = () => {
    getSingleDiary(page_id)
      .then((res) => {
        setData(res);
        setContent(res.note);
      })
      .catch(() => {
        toast.error(
          "Error while fetching the data for Writing Center. Please try again later."
        );
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChanges = (event, property) => {
    setData({ ...data, [property]: event.target.value });
  };

  const handleEditSave = (event) => {
    event.preventDefault();
    stopRecording();

    const updatedDiary = {
      heading: data.heading,
      note: content,
      user: {
        id: getUser().id,
      },
      writingCenter: {
        id: books_id,
      },
    };

    editDiary(page_id, updatedDiary)
//       .then(() => {
          .then(async () => {
          if (audioBlob && audioBlob.size > 0) {
              const formData = new FormData();
              formData.append("file", audioBlob);

              await uploadDiaryAudio(page_id, formData);
            }
        fetchData();
        toast.success("Diary entry updated successfully", {
          position: toast.POSITION.BOTTOM_CENTER,
        });
        window.history.back();
      })
      .catch(() => {
        toast.error("Error updating diary entry. Please try again later.");
      });
  };

  return (
    <div className="popup">
      <div className="popup-content">
        <section className="user_diary_add_note">
          <form className="user_diary_add_note_form" onSubmit={handleEditSave}>
            <a onClick={() => window.history.back()}>
              <div className="login_cancel_icon">
                <FontAwesomeIcon className="cancel_icon_login" icon={faTimes} />
              </div>
            </a>
            <label htmlFor="entry-title" className="user_diary_label">
              Edit Diary Title...
            </label>

            <AutocompleteInput
              value={data.heading}
              onChange={(e) => handleChanges(e, "heading")}
              placeholder="Title of the Entry ✏️"
              className="user_diary_entry_text_title"
            />

            <label htmlFor="entry" className="user_diary_label">
              Edit Your Diary...
            </label>

            <SpeechToTextButton
              isRecording={isRecording}
              toggleRecording={toggleRecording}
            />

            <JoditEditor
              ref={editor}
              value={content}
              config={config}
              onBlur={(newContent) => setContent(newContent)}
            />
            {/* 🎧 EXISTING AUDIO */}
            {page_id && (
              <div style={{ marginTop: "15px", textAlign: "center" }}>
                <p style={{ fontWeight: "bold" }}>Voice Memo:</p>

                <audio controls style={{ width: "200px" }}>
                  <source src={getDiaryAudio(page_id)} type="audio/webm" />
                </audio>
              </div>
            )}

            {/* 🎙️ RECORD NEW AUDIO */}
            <div style={{ marginTop: "10px", textAlign: "center" }}>
              <button
                type="button"
                onClick={isRecordingAudio ? stopRecordingAudio : startRecordingAudio}
                style={{
                  padding: "8px 12px",
                  backgroundColor: isRecordingAudio ? "#dc3545" : "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                {isRecordingAudio ? "Stop Recording ⏹️" : "Replace Voice 🎙️"}
              </button>
            </div>
            <button className="user_diary_submit_btn" type="submit">
              <Tooltip title="Click here to save your edit">Save</Tooltip>
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
